<script lang="ts">
export type DotIndicatorColor = 'green' | 'yellow' | 'purple' | 'red'
</script>

<script lang="ts" setup>
import IconSprite from '@/uiKit/IconSprite.vue'
import ToolTip from '@/uiKit/ToolTip.vue'
import type { AutoPlacementOptions, FlipOptions, OffsetOptions, Placement } from '@floating-ui/vue'
import { computed } from 'vue'

/**
 * Renders a coloured dot used to indicate the status of a resource
 * https://www.figma.com/design/1HfA941cU4A9RZxXHLmbpG/V7-Go---Design-System?node-id=682-20262&m=dev
 */

const props = withDefaults(
  defineProps<{
    color: DotIndicatorColor
    clickable?: boolean
    ariaLabel?: string
    tooltip?: {
      title?: string
      body?: string
      placement?: Placement
      autoPlacementOptions?: AutoPlacementOptions
      flipOptions?: FlipOptions
      offset?: OffsetOptions
    }
  }>(),
  {
    clickable: false,
    ariaLabel: undefined,
    tooltip: undefined,
  },
)

const recalculateIconColor = computed<string>(() => {
  switch (props.color) {
    case 'red':
    case 'yellow':
      return 'text-[white]'
    default:
      return ''
  }
})

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <ToolTip
    :title="tooltip?.title"
    :body="tooltip?.body"
    :disabled="!tooltip?.title && !tooltip?.body"
    :placement="tooltip?.placement"
    :auto-placement-options="tooltip?.autoPlacementOptions"
    :flip-options="tooltip?.flipOptions"
    :offset="tooltip?.offset"
  >
    <button
      v-if="clickable"
      class="group/dot grid size-5 cursor-pointer place-items-center"
      :aria-label="ariaLabel"
      v-bind="$attrs"
    >
      <div
        class="relative grid size-1.5 place-items-center overflow-hidden rounded-full transition-all duration-250 ease-out group-hover/dot:size-5"
        :class="[color === 'red' && ['bg-icon-critical'], color === 'yellow' && ['bg-icon-stale']]"
      >
        <IconSprite
          icon="process"
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-90 text-[12px] opacity-0 transition-all duration-150 ease-out group-hover/dot:rotate-0 group-hover/dot:opacity-100 group-hover/dot:delay-100"
          :class="recalculateIconColor"
        />
      </div>
    </button>
    <div
      v-else
      role="status"
      :aria-label="ariaLabel"
      class="grid size-5 place-items-center"
      v-bind="$attrs"
    >
      <svg
        view-box="0 0 12 12"
        class="size-3 rounded-corner-4 bg-background-transparent hover:bg-background-transparent-hovered active:bg-background-transparent-pressed"
      >
        <circle
          cx="6"
          cy="6"
          r="5"
          class="transition-opacity"
          :class="[
            color === 'purple'
              ? 'animate-pulse fill-background-stages-model opacity-10'
              : 'opacity-0 group-hover/dot:opacity-10',
            color === 'yellow' && ['fill-icon-stale'],
          ]"
        />
        <circle
          cx="6"
          cy="6"
          r="3"
          :class="[
            color === 'green' && 'fill-background-success',
            color === 'purple' && 'fill-background-stages-model',
            color === 'yellow' && 'fill-icon-stale',
            color === 'red' && 'fill-icon-critical',
          ]"
        />
      </svg>
    </div>
  </ToolTip>
</template>
